<script setup lang="ts">
import { type AchievementItem, AchievementType } from '@/types/achievements'
import { formatNumberFullTruncate } from '../helpers/utils'
import { twMerge } from 'tailwind-merge'
import { computed } from 'vue'
import { achievementCategories } from '@/composables/game/achievements'
import { achievementTypeIconMap, levelColors, season2AchievementCategories } from '@/composables/game/season2-achievements'

const props = defineProps<{
	card: AchievementItem
	owned: boolean
	isNew?: boolean
	isPreviousOwned?: boolean
	cardClass?: string
	imageClass?: string
	wrapperClass?: string
	labelClass?: string
	currentValue?: number
	categoryId?: AchievementType
}>()

// Get icon source from S2 mapping based on categoryId
const iconSrc = computed(() => {
  if (props.categoryId) {
    return achievementTypeIconMap[props.categoryId]
  }
  return ''
})

// Look up the level from S2 config based on categoryId and milestone number
const milestoneLevel = computed(() => {
  if (!props.categoryId) return 0
  const milestone = season2AchievementCategories[props.categoryId].milestones.find(
    m => m.number === props.card.number
  )
  return milestone?.level ?? 0
})

// Get color for overlay based on milestone level
const levelColor = computed(() => {
  const level = milestoneLevel.value
  if (!level || level < 1 || level > 11) return null
  return levelColors[level]
})

const tooltipContent = computed(() => {
  if (props.isPreviousOwned && !props.owned) {
    if (!props.categoryId) return ''
    const remainedCount = props.card.number - props.currentValue!
    return achievementCategories[props.categoryId].leftUntilNextMilestoneTooltip(remainedCount)
  }
  // if (!props.card.tooltip || !props.card.percentage) return ''
  // return props.card.tooltip.replace('{percentage}', `<span class="text-linear-gold font-bold">${props.card.percentage}%</span>`)
  return ''
})
</script>

<template>
  <VTooltip
    style="padding: 0 !important;"
    :disabled="!tooltipContent"
    placement="right"
    :triggers="['touch', 'click', 'hover']"
    :popper-triggers="['touch', 'click', 'hover']"
    :distance="4"
    popper-class="achievement-card-tooltip"
    auto-hide
  >
    <template
      #popper
    >
      <span v-html="tooltipContent" />
    </template>
    <div
      :class="twMerge('h-[110px] rounded-xl flex flex-col items-center justify-center relative', cardClass, owned ? 'border-2 border-[#F5C250]' : 'opacity-[0.66] border-2 border-transparent')"
    >
      <div
        v-if="isNew"
        class="absolute -top-2 h-5 left-1/2 -translate-x-1/2 rounded-[6px] z-10 bg-card-completed flex items-center justify-center text-xs font-semibold -tracking-[0.24px] text-black uppercase px-2"
      >
        NEW
      </div>
      <div
        :class="twMerge('flex-1 w-full h-full bg-dark-top-layer rounded-[11px] flex flex-col items-center justify-center p-[7px] gap-y-1 relative overflow-hidden', wrapperClass)"
      >
        <!-- Color overlay at 44% opacity -->
        <div
          v-if="levelColor"
          class="absolute inset-0 rounded-[11px] pointer-events-none"
          :style="{ backgroundColor: levelColor, opacity: 0.44, mixBlendMode: 'overlay' }"
        />
        <img
          :class="twMerge('w-auto h-[64px] max-w-full object-cover my-0.5 pointer-events-none', imageClass)"
          :src="iconSrc"
        >
        <div
          :class="twMerge('whitespace-nowrap text-base font-semibold -tracking-[0.32px] text-white/[.87] relative z-[1]', labelClass)"
        >
          {{ formatNumberFullTruncate(card.number) }}
          <template v-if="card.label">
            {{ card.label }}
          </template>
        </div>
      </div>
    </div>
  </VTooltip>
</template>

<style scoped lang="scss">
.bg-card-completed {
	background: linear-gradient(90deg, #FFF246 0%, #E5B80B 37%, #FFF571 68.5%, #E5B80B 100%);
}

.bg-card-incompleted {
	background: linear-gradient(90deg, rgba(255, 255, 255, 0.4) 0%, rgba(255, 255, 255, 1) 50%, rgba(255, 255, 255, 0.65) 100%);
}
</style>
<style lang="scss">
.text-linear-gold {
	background: var(--gold, linear-gradient(90deg, #FFF246 0%, #E5B80B 37%, #FFF571 68.5%, #E5B80B 100%));
	-webkit-background-clip: text;
	-webkit-text-fill-color: transparent;
	display: inline-block;
}
</style>
<style lang="scss">
.achievement-card-tooltip {
	&.v-popper--theme-tooltip {

		.v-popper__inner {
			@apply text-left whitespace-pre bg-dark-top-most-layer rounded-[4px] px-3 py-2 text-sm font-bold max-w-[168px] #{!important};
		}

		.v-popper__arrow-outer {
			@apply border-dark-top-most-layer #{!important}
		}

		.v-popper__arrow-inner {
			@apply border-dark-top-most-layer #{!important}
		}
	}
}
</style>